import {Directive, effect, ElementRef, input} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {erIkkeTomStreng} from "felles-ng2/støttefunksjoner/strenger";

const debug = initDebugger('VisSpinnerDirektiv');

/**
 * Viser spinner dersom visSpinner=true.
 * Viser erstatter også eksisterende label med angitt spinnerTekst dersom slik er angitt.
 *
 * Eks: `<button mat-raised-button [visSpinner]="true" spinnerTekst="Lagrer">En knapp</button>`
 */
@Directive({
    selector: 'button[visSpinner]',
    standalone: true
})
export class VisSpinnerDirektiv {

    /** antall millisekunder vi venter fra setSpinner==true til spinner vises */
    static readonly visSpinnerForsinkelse = 100;

    /** Dersom sann vises spinner og eksisterende label ersattes av angitt spinnerTekst, dersom slik er angitt */
    visSpinner = input.required<boolean>()

    /** Tekst som vises istedenfor eksisterende label dersom {@link #visSpinner} er sann */
    spinnerTekst = input('');

    private spinnerElement: HTMLSpanElement | null = null;
    private buttonLabelElement: HTMLSpanElement | null = null;
    private spinnerLabelElement: HTMLSpanElement | null = null;
    private buttonOpprinneligMinWidth  = '';

    private spinnerTimeout: number | null = null;

    constructor(private el: ElementRef<HTMLButtonElement>) {
        effect(() => {
            (this.visSpinner()) ? this.aktiverSpinner() : this.gjørSkjult();
            if (this.visSpinner() && erIkkeTomStreng(this.spinnerTekst()))
                this.visSpinnerTekst(this.spinnerTekst())
        });
    }

    //private hjelpemetoder

    private aktiverSpinner() {
        if (this.spinnerElement == null) this.leggTilSpinnerElement();
        //ikke vis spinner før det er gått 100ms
        this.spinnerTimeout = window.setTimeout(() => this.gjørSynlig(), VisSpinnerDirektiv.visSpinnerForsinkelse);
    }

    private gjørSynlig() {
        const initiellButtonBredde = this.buttonElement().offsetWidth;
        this.spinnerElement!.hidden = false;
        if (this.spinnerLabelElement != null) {
            this.buttonOpprinneligMinWidth = this.buttonElement().style.minWidth;
            this.buttonElement().style.minWidth = `${initiellButtonBredde}px`;
            this.spinnerLabelElement.hidden = false;
            this.buttonLabelElement!.hidden = true;
        }
    }

    private gjørSkjult() {
        if (this.spinnerElement == null) return;
        if (erIkkeNil(this.spinnerTimeout)) window.clearTimeout(this.spinnerTimeout);
        this.spinnerElement.hidden = true;
        if (this.spinnerLabelElement != null) {
            this.buttonElement().style.minWidth = this.buttonOpprinneligMinWidth;
            this.spinnerLabelElement.hidden = true;
            this.buttonLabelElement!.hidden = false;
        }
    }

    private leggTilSpinnerElement() {
        debug('oppretter spinner')
        this.buttonLabelElement = this.buttonElement().querySelector('.mdc-button__label');
        debug('labelElement', this.buttonLabelElement)
        this.spinnerElement = document.createElement('span');
        this.spinnerElement.classList.add('sp-button--spinner')
        this.spinnerElement.setAttribute('data-testid', 'sp-spinner')
        this.spinnerElement.style.paddingRight = '16px';
        this.spinnerElement.hidden = true;
        this.spinnerElement.innerHTML = spinnerHtmlAccent;
        this.buttonElement().insertBefore(this.spinnerElement, this.buttonLabelElement);
    }

    private visSpinnerTekst(tekst: string) {
        if (erNil(this.spinnerLabelElement))
            this.leggTilSpinnerLabelElement();

        //burde egentlig bruker innerText, men da feiler jestspec test (elementet fremstår som ikke å ha noen tekst)
        //dette problemet blir borte ved bruk av innerHTML
        //PS: innerText bør brukes fremfor textContent da textContent bla gjør om linjeskift i tekst til <br>
        this.spinnerLabelElement!.innerHTML = tekst;
    }

    private leggTilSpinnerLabelElement() {
        debug('oppretter spinnerLabelElement')
        this.spinnerLabelElement = document.createElement('span');
        this.spinnerLabelElement.hidden = true;
        this.spinnerLabelElement.classList.add('mdc-button__label', 'sp-button--spinner-label');
        this.spinnerLabelElement.setAttribute('data-testid', 'sp-label')
        this.buttonElement().insertBefore(this.spinnerLabelElement, this.buttonLabelElement);
    }

    private buttonElement() {
        return this.el.nativeElement;
    }
}

/** html generer av <mat-icon><mat-spinner color="accent" diameter="20"></mat-spinner></mat-icon> */
const spinnerHtmlAccent = `
<span role="progressbar" tabindex="-1" 
      class="mat-mdc-progress-spinner mdc-circular-progress mat-accent mdc-circular-progress--indeterminate"
      style="width: 20px; height: 20px; --mat-progress-spinner-size: 20px; --mat-progress-spinner-active-indicator-width: 20px;">
  <div aria-hidden="true" class="mdc-circular-progress__determinate-container">
    <svg xmlns="http://www.w3.org/2000/svg" focusable="false" class="mdc-circular-progress__determinate-circle-graphic" viewBox="0 0 12 12">
      <circle cx="50%" cy="50%" class="mdc-circular-progress__determinate-circle" r="5" style="stroke-dasharray: 31.4159px; stroke-width: 10%;"></circle>
    </svg>
  </div>
  <div aria-hidden="true" class="mdc-circular-progress__indeterminate-container">
    <div class="mdc-circular-progress__spinner-layer">
      <div class="mdc-circular-progress__circle-clipper mdc-circular-progress__circle-left">
        <svg xmlns="http://www.w3.org/2000/svg" focusable="false" class="mdc-circular-progress__indeterminate-circle-graphic ng-star-inserted" viewBox="0 0 12 12">
          <circle cx="50%" cy="50%" r="5" style="stroke-dasharray: 31.4159px; stroke-dashoffset: 15.708px; stroke-width: 10%;"></circle>
        </svg><!--ng-container--></div>
      <div class="mdc-circular-progress__gap-patch">
        <svg xmlns="http://www.w3.org/2000/svg" focusable="false" class="mdc-circular-progress__indeterminate-circle-graphic ng-star-inserted" viewBox="0 0 12 12">
          <circle cx="50%" cy="50%" r="5" style="stroke-dasharray: 31.4159px; stroke-dashoffset: 15.708px; stroke-width: 10%;"></circle>
        </svg><!--ng-container--></div>
      <div class="mdc-circular-progress__circle-clipper mdc-circular-progress__circle-right">
        <svg xmlns="http://www.w3.org/2000/svg" focusable="false" class="mdc-circular-progress__indeterminate-circle-graphic ng-star-inserted" viewBox="0 0 12 12">
          <circle cx="50%" cy="50%" r="5" style="stroke-dasharray: 31.4159px; stroke-dashoffset: 15.708px; stroke-width: 10%;"></circle>
        </svg><!--ng-container--></div>
    </div>
  </div>
</span>
`
